import { VehicleType } from '@thive/fddm-types';
import { t } from 'i18next';

import { FileServiceConnector, IFileServiceConnector } from '@/modules/api/FileApiConnector';
import { IPIMServiceConnector, PIMServiceConnector } from '@/modules/api/PimApiConnector';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { assertNever } from '@/helpers/utils';
import { logger } from '@common/logging/logger';
import { buildRevisionFileStorageBasePath } from '@/modules/common/helpers/buildRevisionFileStorageBasePath';
import {
  PROJECT_FILES_ROOT_CONTAINER,
  KM_MDB_ASSET_FILE_CONTAINER,
  UVT_ASSET_FILE_CONTAINER,
} from '@/modules/api/constant';

import { isVehicleAssetFileRef } from '../fileReference';
import { useVehicleStore } from '../store/useVehicleStore';
import {
  FileReferenceResponse,
  isSyncAssetFileSuccessResponse,
  SyncOriginalAssetFileFailed,
  SyncOriginalAssetFileSuccess,
  UnifiedVehicle,
  UnifiedVehicleDetails,
  ValidAssetType,
} from '../types';
import { IVehicleService } from './IVehicleService';
import { VehicleAssetSyncRequest, VehicleDetailsResult, UvtAsset } from '../types/internal';

export class VehicleDependencyServiceImpl implements IVehicleService {
  private readonly vehicleStore: typeof useVehicleStore;
  private readonly pimSvc: IPIMServiceConnector;
  private readonly fileSvc: IFileServiceConnector;

  public constructor(vehicleStore: typeof useVehicleStore) {
    this.vehicleStore = vehicleStore;
    this.pimSvc = PIMServiceConnector.getInstance();
    this.fileSvc = FileServiceConnector.getInstance();
  }

  public async loadVehicles(): Promise<void> {
    const vehicles = await this.pimSvc.getAllUnifiedVehicles();
    this.vehicleStore.getState().allVehicles.set(vehicles);
  }

  public getVehicle(vehicleId: string): UnifiedVehicle {
    const vehicle = this.vehicleStore.getState().allVehicles.getByIds([vehicleId])[0];

    if (!vehicle) {
      throw new Error(`Vehicle with id ${vehicleId} not found`);
    }

    return vehicle;
  }

  /** @description fetches and parses a vehicle json asset from file api by file reference. */
  public async getVehicleJSONAsset(
    reference: string,
    errorMessage?: string,
  ): Promise<unknown | null> {
    try {
      const basePath = buildRevisionFileStorageBasePath();
      if (!basePath) {
        logger.error('Could not get vehicle JSON asset. basePath not defined.');
        return;
      }

      return await this.fileSvc.getJsonFileByUrl(
        `${PROJECT_FILES_ROOT_CONTAINER}?filename=${basePath}/${reference}`,
        errorMessage,
      );
    } catch (error) {
      logger.error('Error fetching unified vehicle type asset: ', error);
      return null;
    }
  }

  /** @description requests pim to sync a vehicle asset to file api and returns uploaded references. */
  public async fetchAndUploadAssetToFileApi(
    vehicleId: string,
    projectId: string,
    layoutAlternativeId: string,
    revisionId: string,
    assetType?: ValidAssetType,
  ): Promise<FileReferenceResponse | null> {
    // TODO: prevent duplicating a new file for every single vehicle...
    let category: ValidAssetType | undefined;

    switch (assetType) {
      case 'uvt':
        category = UVT_ASSET_FILE_CONTAINER;
        break;

      case 'km_mdb':
        category = KM_MDB_ASSET_FILE_CONTAINER;
        break;

      case undefined:
        category = undefined;
        break;

      default:
        assertNever(assetType);
    }

    return this.pimSvc.getUnifiedVehicleAssetUrl(
      vehicleId,
      projectId,
      layoutAlternativeId,
      revisionId,
      category,
    );
  }

  /** @description ensures required uvt and km/mdb references exist for the provided vehicle ids. */
  public async ensureVehicleAssetAvailability(
    vehicleIds: string[],
    projectId: string,
    layoutAlternativeId: string,
    revisionId: string,
    floorplanVehicleTypes?: VehicleType[],
  ): Promise<VehicleType[]> {
    const enabledVehicles = this.vehicleStore.getState().allVehicles.getByIds(vehicleIds);
    const allVehicleTypes =
      floorplanVehicleTypes ?? useFloorplanDesignStore.getState().getVehicleTypes();

    const vehicleTypesById = new Map(
      allVehicleTypes.map((vehicleType) => [vehicleType.id, vehicleType]),
    );

    const missingTypes: VehicleAssetSyncRequest[] = [];

    enabledVehicles.forEach((vehicle) => {
      const currentAsset = vehicleTypesById.get(vehicle.id);

      const uvtMissing =
        !currentAsset ||
        !currentAsset.uvtFileRef ||
        !isVehicleAssetFileRef(currentAsset.uvtFileRef, vehicle.id, UVT_ASSET_FILE_CONTAINER);

      let kmMdbMissing =
        !currentAsset ||
        !currentAsset.kmMdbFileRef ||
        !isVehicleAssetFileRef(currentAsset.kmMdbFileRef, vehicle.id, KM_MDB_ASSET_FILE_CONTAINER);

      if (!vehicle.kmMdb.path) {
        kmMdbMissing = false;
      }

      if (!uvtMissing && !kmMdbMissing) {
        return;
      }

      missingTypes.push({
        vehicle,
        uvtMissing,
        kmMdbMissing,
        projectId,
        layoutAlternativeId,
        revisionId,
      });
    });

    const syncResults = await Promise.all(
      missingTypes.map((item) => this.syncOriginalAssetFileToFileStorage(item)),
    );

    const failedSyncResults = syncResults.filter((result) => !result.success);

    if (failedSyncResults.length > 0) {
      logger.error(
        'Something went wrong while trying to ensure vehicle asset availability. Failed: ',
        failedSyncResults.map((item) => ({
          vehicleId: item.vehicleId,
        })),
      );
    }

    const successfulSyncResults = syncResults.filter(isSyncAssetFileSuccessResponse);

    successfulSyncResults.forEach((result) => {
      const existingVehicleType = vehicleTypesById.get(result.vehicleId);

      const nextVehicleType: VehicleType = {
        id: result.vehicleId,
        uvtFileRef: result.uvtReference ?? existingVehicleType?.uvtFileRef ?? '',
        uvtFileRefOriginal: existingVehicleType?.uvtFileRefOriginal ?? true,
        kmMdbFileRef: result.kmMdbFileRef ?? existingVehicleType?.kmMdbFileRef ?? '',
        kmMdbFileRefOriginal: existingVehicleType?.kmMdbFileRefOriginal ?? true,
        splineOptimizerSettings: existingVehicleType?.splineOptimizerSettings ?? {
          enableObstacleAvoidance: false,
        },
      };

      vehicleTypesById.set(result.vehicleId, nextVehicleType);
    });

    const enabledVehicleTypes = vehicleIds
      .map((vehicleId) => vehicleTypesById.get(vehicleId))
      .filter((vehicleType): vehicleType is VehicleType => Boolean(vehicleType));

    enabledVehicleTypes.forEach((vehicleType) => {
      this.storeTypeReference(vehicleType);
    });

    return enabledVehicleTypes;
  }

  /** @description loads missing vehicle dimension details from synced uvt assets for the provided ids. */
  public async ensureVehicleDetailsAvailability(
    vehicleIds: string[],
    floorplanVehicleTypes?: VehicleType[],
  ): Promise<void> {
    const vehicles = this.vehicleStore.getState().allVehicles.getByIds(vehicleIds);
    const allVehicleAssets =
      floorplanVehicleTypes ?? useFloorplanDesignStore.getState().getVehicleTypes();

    const knownVehicleDetails = this.vehicleStore.getState().vehicleDetails.getByIds(vehicleIds);
    const knownVehicleDetailsIds = new Set(knownVehicleDetails.map((item) => item.id));

    const detailResults = await Promise.all(
      vehicles
        .filter((vehicle) => !knownVehicleDetailsIds.has(vehicle.id))
        .map((vehicle) => this.loadVehicleDetails(vehicle, allVehicleAssets)),
    );

    const loadedDetails: UnifiedVehicleDetails[] = [];
    const missingDetailsVehicleIds: string[] = [];

    detailResults.forEach((result) => {
      if (!result.success) {
        missingDetailsVehicleIds.push(result.vehicleId);
        return;
      }

      loadedDetails.push(result.details);
    });

    if (missingDetailsVehicleIds.length > 0) {
      logger.error(
        'Failed to retrieve vehicle details for vehicles with ids: ',
        missingDetailsVehicleIds,
      );
    }

    const nextVehicleDetailsById = new Map(knownVehicleDetails.map((item) => [item.id, item]));

    loadedDetails.forEach((item) => {
      nextVehicleDetailsById.set(item.id, item);
    });

    this.setVehicleDetails(Array.from(nextVehicleDetailsById.values()));
  }

  /** @description reconciles enabled vehicle types by syncing assets, details, and removing stale entries. */
  public async reconcileEnabledVehicleTypes(
    vehicleIds: string[],
    projectId: string,
    layoutAlternativeId: string,
    revisionId: string,
    addNewVehiclesToHighways = false,
  ): Promise<VehicleType[]> {
    const currentVehicleTypes = useFloorplanDesignStore.getState().getVehicleTypes();
    const currentVehicleIds = currentVehicleTypes.map((vehicleType) => vehicleType.id);

    const removedVehicleIds = currentVehicleIds.filter(
      (vehicleId) => !vehicleIds.includes(vehicleId),
    );

    const addedVehicleIds = vehicleIds.filter(
      (vehicleId) => !currentVehicleIds.includes(vehicleId),
    );

    const enabledVehicleTypes = await this.ensureVehicleAssetAvailability(
      vehicleIds,
      projectId,
      layoutAlternativeId,
      revisionId,
      currentVehicleTypes,
    );

    await this.ensureVehicleDetailsAvailability(vehicleIds, enabledVehicleTypes);

    const fddmSvc = FddmServiceFactory.getInstance().createFddmService();

    if (removedVehicleIds.length > 0) {
      const removedVehicleTypes = currentVehicleTypes.filter((vehicleType) =>
        removedVehicleIds.includes(vehicleType.id),
      );

      fddmSvc.deleteVehicleTypes(removedVehicleTypes);
      this.removeVehicleDetails(removedVehicleIds);
    }

    if (addNewVehiclesToHighways && addedVehicleIds.length > 0) {
      const addedVehicleTypes = enabledVehicleTypes.filter((vehicleType) =>
        addedVehicleIds.includes(vehicleType.id),
      );

      fddmSvc.addVehiclesToHighways(addedVehicleTypes);
    }

    this.cleanUpRedundantTypes();

    return enabledVehicleTypes;
  }

  public cleanUpRedundantTypes(): void {
    const { getVehicleTypeIds, getVehicleTypes } = useFloorplanDesignStore.getState();

    const enabledVehicleIds = getVehicleTypeIds();
    const redundantVehicleTypes = getVehicleTypes().filter(
      (vehicleType) => !enabledVehicleIds.includes(vehicleType.id),
    );

    if (redundantVehicleTypes.length === 0) {
      return;
    }

    FddmServiceFactory.getInstance().createFddmService().deleteVehicleTypes(redundantVehicleTypes);
  }

  public setVehicleDetails(vehicleDetails: UnifiedVehicleDetails[]): void {
    this.vehicleStore.getState().vehicleDetails.set(vehicleDetails);
  }

  public removeVehicleDetails(vehicleIds: string[]): void {
    if (vehicleIds.length === 0) {
      return;
    }

    const vehicleIdsSet = new Set(vehicleIds);
    const { vehicleDetails } = this.vehicleStore.getState();

    const remainingVehicleDetails = vehicleDetails
      .getAll()
      .filter((item) => !vehicleIdsSet.has(item.id));

    vehicleDetails.set(remainingVehicleDetails);
  }

  /** @description syncs missing original asset files for a vehicle and returns resulting references. */
  private async syncOriginalAssetFileToFileStorage({
    vehicle,
    uvtMissing,
    kmMdbMissing,
    projectId,
    layoutAlternativeId,
    revisionId,
  }: VehicleAssetSyncRequest): Promise<SyncOriginalAssetFileSuccess | SyncOriginalAssetFileFailed> {
    let assetType: ValidAssetType | undefined;

    if (uvtMissing && !kmMdbMissing) {
      assetType = 'uvt';
    }

    if (!uvtMissing && kmMdbMissing) {
      assetType = 'km_mdb';
    }

    const references = await this.fetchAndUploadAssetToFileApi(
      vehicle.id,
      projectId,
      layoutAlternativeId,
      revisionId,
      assetType,
    );

    if (references && (references.uvt || references.km_mdb)) {
      return {
        success: true,
        vehicleId: vehicle.id,
        uvtReference: references.uvt ?? null,
        kmMdbFileRef: references.km_mdb ?? null,
      };
    }

    return {
      success: false,
      vehicleId: vehicle.id,
    };
  }

  /** @description derives vehicle dimensions and safety margin from the first uvt vehicle type physics. */
  private async loadVehicleDetails(
    vehicle: UnifiedVehicle,
    vehicleTypes: VehicleType[],
  ): Promise<VehicleDetailsResult> {
    const currentAsset = vehicleTypes.find((vehicleType) => vehicleType.id === vehicle.id);

    if (!currentAsset) {
      return {
        success: false,
        vehicleId: vehicle.id,
      };
    }

    const asset = await this.getVehicleJSONAsset(
      currentAsset.uvtFileRef,
      t('errors:files.error_fetching_uvt_file', { vehicleTypeId: vehicle.id }),
    );
    const uvtAsset = this.isUvtAsset(asset) ? asset : undefined;
    const vehicleType = uvtAsset?.vehicleTypes?.[0];
    const physics = vehicleType?.physics;
    const loadHandler = vehicleType?.loadHandler;

    if (
      !physics?.vehicleShape ||
      physics.positionalAccuracy === undefined ||
      loadHandler?.loadTypes === undefined
    ) {
      return {
        success: false,
        vehicleId: vehicle.id,
      };
    }

    return {
      success: true,
      vehicleId: vehicle.id,
      details: {
        id: vehicle.id,
        databaseId: vehicle.databaseId,
        length: Math.round(
          (Math.abs(physics.vehicleShape.xs[0]) + Math.abs(physics.vehicleShape.xs[1])) * 1000,
        ),
        width: Math.round(
          (Math.abs(physics.vehicleShape.ys[0]) + Math.abs(physics.vehicleShape.ys[1])) * 1000,
        ),
        safetyMargin: Math.round(physics.positionalAccuracy * 1000),
        omni:
          vehicle.category !== null
            ? vehicle.category === 'OMNI_DRIVE'
            : vehicleType?.category === 'OMNI_DRIVE' || physics.drivingMode === 'omni',
        loadTypes: loadHandler.loadTypes.map((loadType) => loadType.loadTypeRef),
      },
    };
  }

  private storeTypeReference(value: VehicleType): void {
    const vehicleType: VehicleType = {
      id: value.id,
      uvtFileRef: value.uvtFileRef,
      uvtFileRefOriginal: value.uvtFileRefOriginal,
      kmMdbFileRef: value.kmMdbFileRef,
      kmMdbFileRefOriginal: value.kmMdbFileRefOriginal,
      splineOptimizerSettings: value.splineOptimizerSettings,
    };

    const fddmSvc = FddmServiceFactory.getInstance().createFddmService();

    if (!useFloorplanDesignStore.getState().getVehicleTypeById(value.id)) {
      fddmSvc.addVehicleTypes([vehicleType]);
      return;
    }

    fddmSvc.updateVehicleType(vehicleType);
  }

  private isUvtAsset(value: unknown): value is UvtAsset {
    if (!value || typeof value !== 'object') {
      return false;
    }

    return 'vehicleTypes' in value;
  }

  public async getLatestFileRef(
    assetType: ValidAssetType,
    projectId: string,
    layoutAlternativeId: string,
    revisionId: string,
    vehicleId: string,
  ): Promise<void> {
    const fddmSvc = FddmServiceFactory.getInstance().createFddmService();

    switch (assetType) {
      case 'uvt': {
        const assetReference = await this.syncOriginalAssetFileToFileStorage({
          vehicle: this.getVehicle(vehicleId),
          uvtMissing: true,
          kmMdbMissing: false,
          projectId,
          layoutAlternativeId,
          revisionId,
        });

        if (!isSyncAssetFileSuccessResponse(assetReference)) {
          return;
        }

        const { uvtReference } = assetReference;
        if (uvtReference === null) {
          return;
        }

        const vehicleType = useFloorplanDesignStore
          .getState()
          .getVehicleTypeByIdSilently(vehicleId);
        if (!vehicleType) {
          return;
        }

        fddmSvc.updateVehicleType({
          ...vehicleType,
          uvtFileRef: uvtReference,
          uvtFileRefOriginal: true,
        });

        // Re-read the new UVT so vehicle details are refreshed.
        this.removeVehicleDetails([vehicleId]);
        const updatedVehicleTypes = useFloorplanDesignStore.getState().getVehicleTypes();
        await this.ensureVehicleDetailsAvailability([vehicleId], updatedVehicleTypes);

        return;
      }

      case 'km_mdb': {
        const assetReference = await this.syncOriginalAssetFileToFileStorage({
          vehicle: this.getVehicle(vehicleId),
          uvtMissing: false,
          kmMdbMissing: true,
          projectId,
          layoutAlternativeId,
          revisionId,
        });

        if (!isSyncAssetFileSuccessResponse(assetReference)) {
          return;
        }

        const { kmMdbFileRef } = assetReference;
        if (kmMdbFileRef === null) {
          return;
        }

        const vehicleType = useFloorplanDesignStore
          .getState()
          .getVehicleTypeByIdSilently(vehicleId);
        if (!vehicleType) {
          return;
        }

        fddmSvc.updateVehicleType({
          ...vehicleType,
          kmMdbFileRef,
          kmMdbFileRefOriginal: true,
        });

        return;
      }

      default:
        assertNever(assetType);
    }
  }

  public async getVehicleTypeFiles(vehicleTypes: VehicleType[]): Promise<{
    uvtFiles: object[];
    uvtFileNames: string[];
    kmMdbFiles: Blob[];
    kmMdbFileNames: string[];
  }> {
    const uvtFilesPromises: Promise<object>[] = [];
    const uvtFileNames: string[] = [];
    const kmMdbFilesPromises: Promise<Blob>[] = [];
    const kmMdbFileNames: string[] = [];

    vehicleTypes.forEach((vehicleType) => {
      const basePath = buildRevisionFileStorageBasePath();
      if (!basePath) {
        logger.error('Could not get vehicle files. basePath not defined.');
        return;
      }

      if (vehicleType.kmMdbFileRef) {
        kmMdbFileNames.push(vehicleType.id);

        kmMdbFilesPromises.push(
          this.fileSvc.getBlobFileByUrl(
            `${PROJECT_FILES_ROOT_CONTAINER}?filename=${basePath}/${vehicleType.kmMdbFileRef}`,
          ),
        );
      }

      if (vehicleType.uvtFileRef) {
        uvtFileNames.push(vehicleType.id);
        uvtFilesPromises.push(
          this.fileSvc.getJsonFileByUrl(
            `${PROJECT_FILES_ROOT_CONTAINER}?filename=${basePath}/${vehicleType.uvtFileRef}`,
            t('errors:files.error_fetching_uvt_file', { vehicleTypeId: vehicleType.id }),
          ),
        );
      }
    });

    const [kmMdbFiles, uvtFiles] = await Promise.all([
      Promise.all(kmMdbFilesPromises),
      Promise.all(uvtFilesPromises),
    ]);

    return {
      uvtFiles,
      uvtFileNames,
      kmMdbFiles,
      kmMdbFileNames,
    };
  }
}
